"use client";

import {
  type ChangeEvent,
  useDeferredValue,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import { decodeRisBytes } from "@/lib/tool-kit/identifiers/ris-check";
import type { ConvertResult } from "@/lib/tool-kit/research-discovery/convert";
import {
  type RefRecord,
  recordsToBibtex,
  recordsToCslJson,
  recordsToRis,
} from "@/lib/tool-kit/research-discovery/records";

/** The strings every converter widget supplies to this panel. */
export interface ConverterStrings {
  inputLabel: string;
  inputPlaceholder: string;
  inputHint: string;
  upload: string;
  uploadLabel: string;
  trySample: string;
  sample: string;
  clear: string;
  fileRead: string;
  encodings: { utf8: string; utf16: string; ansi: string };
  emptyPrompt: string;
  results: string;
  statFound: string;
  statConverted: string;
  statDoi: string;
  statWarnings: string;
  formatLabel: string;
  formats: { ris: string; bibtex: string; csl: string };
  outputHeading: string;
  previewCut: string;
  copyOutput: string;
  copied: string;
  download: string;
  warningsHeading: string;
  warningsCut: string;
  warnings: Record<string, string>;
  whereRecord: string;
  whereFile: string;
  problems: Record<string, string>;
  tableHeading: string;
  tableCut: string;
  columns: { type: string; authors: string; year: string; title: string };
  etAl: string;
  untitled: string;
}

type Format = "ris" | "bibtex" | "csl";

const PREVIEW_LINES = 200;
const TABLE_ROWS = 20;
const WARNING_ROWS = 50;
const RE_EXTENSION = /\.[^.]+$/;
const RE_EOL = /\r\n|\r|\n/;

const OUTPUTS: Record<
  Format,
  {
    extension: string;
    mime: string;
    write: (r: readonly RefRecord[]) => string;
  }
> = {
  ris: {
    extension: "ris",
    mime: "application/x-research-info-systems;charset=utf-8",
    write: recordsToRis,
  },
  bibtex: {
    extension: "bib",
    mime: "application/x-bibtex;charset=utf-8",
    write: recordsToBibtex,
  },
  csl: {
    extension: "json",
    mime: "application/json;charset=utf-8",
    write: recordsToCslJson,
  },
};

const ENCODING_KEY = {
  "utf-8": "utf8",
  "utf-16le": "utf16",
  "utf-16be": "utf16",
  "windows-1252": "ansi",
} as const;

function authorsText(record: RefRecord, etAl: string): string {
  const shown = record.authors
    .slice(0, 3)
    .map((author) => author.literal ?? author.family);
  return record.authors.length > 3
    ? `${shown.join(", ")} ${etAl}`
    : shown.join(", ");
}

export function ConverterPanel({
  strings,
  idPrefix,
  accept,
  convert,
  baseName,
}: {
  strings: ConverterStrings;
  idPrefix: string;
  accept: string;
  convert: (text: string) => ConvertResult;
  baseName: string;
}) {
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [file, setFile] = useState<{ name: string; encoding: string }>();
  const [format, setFormat] = useState<Format>("ris");
  const upload = useRef<HTMLInputElement>(null);
  const deferred = useDeferredValue(text);

  const result = useMemo(() => convert(deferred), [convert, deferred]);
  const output = useMemo(
    () => OUTPUTS[format].write(result.records),
    [format, result.records]
  );

  function edit(value: string) {
    setText(value);
    setFile(undefined);
  }

  async function readFile(event: ChangeEvent<HTMLInputElement>) {
    const chosen = event.target.files?.[0];
    event.target.value = "";
    if (!chosen) {
      return;
    }
    const decoded = decodeRisBytes(new Uint8Array(await chosen.arrayBuffer()));
    setText(decoded.text);
    setFile({ name: chosen.name, encoding: decoded.encoding });
  }

  const hasText = deferred.trim() !== "";
  const lines = output.split(RE_EOL);
  const fileBase = file ? file.name.replace(RE_EXTENSION, "") : baseName;
  const withDoi = result.records.filter((record) => record.doi !== "").length;
  const name = `${fileBase}.${OUTPUTS[format].extension}`;

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id={`${idPrefix}-input`}
        label={strings.inputLabel}
        onChange={edit}
        placeholder={strings.inputPlaceholder}
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => upload.current?.click()}
          type="button"
        >
          {strings.upload}
        </button>
        <label className="sr-only" htmlFor={`${idPrefix}-file`}>
          {strings.uploadLabel}
        </label>
        <input
          accept={accept}
          className="hidden"
          id={`${idPrefix}-file`}
          onChange={readFile}
          ref={upload}
          tabIndex={-1}
          type="file"
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() => edit(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text === ""}
          onClick={() => edit("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {file ? (
        <p className={HINT}>
          {t(strings.fileRead, {
            name: file.name,
            encoding:
              strings.encodings[
                ENCODING_KEY[file.encoding as keyof typeof ENCODING_KEY]
              ],
          })}
        </p>
      ) : null}

      <div aria-live="polite" className="space-y-6">
        {hasText ? null : <Notice>{strings.emptyPrompt}</Notice>}
        {hasText && result.problem ? (
          <ErrorNotice>
            {strings.problems[result.problem] ?? result.problem}
          </ErrorNotice>
        ) : null}
        {hasText && !result.problem ? (
          <div className={`${RESULT_CARD} space-y-5`}>
            <h2 className={WIDGET_H2}>{strings.results}</h2>
            <StatGrid>
              <StatCard
                label={strings.statFound}
                value={fmt(result.sourceCount)}
              />
              <StatCard
                emphasis
                label={strings.statConverted}
                value={fmt(result.records.length)}
              />
              <StatCard label={strings.statDoi} value={fmt(withDoi)} />
              <StatCard
                label={strings.statWarnings}
                value={fmt(result.warnings.length)}
              />
            </StatGrid>
            <PillGroup
              label={strings.formatLabel}
              name={`${idPrefix}-format`}
              onChange={setFormat}
              options={[
                { value: "ris", label: strings.formats.ris },
                { value: "bibtex", label: strings.formats.bibtex },
                { value: "csl", label: strings.formats.csl },
              ]}
              value={format}
            />
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className={WIDGET_H3}>{strings.outputHeading}</h3>
                <div className="flex flex-wrap gap-3">
                  <CopyButton
                    copiedLabel={strings.copied}
                    label={strings.copyOutput}
                    text={output}
                  />
                  <button
                    className={BUTTON_PRIMARY}
                    disabled={output === ""}
                    onClick={() =>
                      downloadText(name, output, OUTPUTS[format].mime)
                    }
                    type="button"
                  >
                    {t(strings.download, { file: name })}
                  </button>
                </div>
              </div>
              <pre className="max-h-96 overflow-auto rounded-lg border border-border bg-card p-4 font-mono text-foreground text-xs leading-relaxed">
                {lines.slice(0, PREVIEW_LINES).join("\n")}
              </pre>
              {lines.length > PREVIEW_LINES ? (
                <p className={HINT}>
                  {t(strings.previewCut, {
                    shown: fmt(PREVIEW_LINES),
                    total: fmt(lines.length),
                  })}
                </p>
              ) : null}
            </div>
            <Warnings result={result} strings={strings} />
            <RecordTable result={result} strings={strings} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

function Warnings({
  result,
  strings,
}: {
  result: ConvertResult;
  strings: ConverterStrings;
}) {
  const fmt = useToolNumber();
  if (result.warnings.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.warningsHeading}</h3>
      <ul className="space-y-1 text-sm">
        {result.warnings.slice(0, WARNING_ROWS).map((warning) => (
          <li
            className="text-foreground"
            key={`${warning.record}-${warning.code}-${warning.detail ?? ""}`}
          >
            <span className="text-muted-foreground">
              {warning.record > 0
                ? t(strings.whereRecord, { record: fmt(warning.record) })
                : strings.whereFile}
              {": "}
            </span>
            {t(strings.warnings[warning.code] ?? warning.code, {
              detail: warning.detail ?? "",
            })}
          </li>
        ))}
      </ul>
      {result.warnings.length > WARNING_ROWS ? (
        <p className={HINT}>
          {t(strings.warningsCut, {
            shown: fmt(WARNING_ROWS),
            total: fmt(result.warnings.length),
          })}
        </p>
      ) : null}
    </div>
  );
}

function RecordTable({
  result,
  strings,
}: {
  result: ConvertResult;
  strings: ConverterStrings;
}) {
  const fmt = useToolNumber();
  if (result.records.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              {[
                strings.columns.type,
                strings.columns.authors,
                strings.columns.year,
                strings.columns.title,
              ].map((heading) => (
                <th className={TH} key={heading} scope="col">
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.records.slice(0, TABLE_ROWS).map((record, index) => (
              // Records have no identity beyond their position in the file.
              // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
              <tr key={index}>
                <td className={`${TD} font-mono text-xs`}>{record.type}</td>
                <td className={TD}>{authorsText(record, strings.etAl)}</td>
                <td className={TD}>{record.year}</td>
                <td className={`${TD} min-w-48`}>
                  {record.title || strings.untitled}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {result.records.length > TABLE_ROWS ? (
        <p className={HINT}>
          {t(strings.tableCut, {
            shown: fmt(TABLE_ROWS),
            total: fmt(result.records.length),
          })}
        </p>
      ) : null}
    </div>
  );
}
